import { useEffect, useReducer, useRef } from "react";
import { getCookie } from "../utils/cookies";
const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;

// Only call the CSRF cookie endpoint if the CSRF cookie is missing
export async function getCsrfCookie() {
  if (
    !document.cookie.split("; ").find((row) => row.startsWith("XSRF-TOKEN="))
  ) {
    return fetch(`${apiEndpoint}/sanctum/csrf-cookie`, {
      credentials: "include",
    });
  }
  // If already present, resolve immediately
  return Promise.resolve();
}

export const GET = (url) => {
  const cache = useRef({});

  const initialState = {
    status: "idle",
    error: null,
    data: [],
    statusCode: null,
  };

  const [state, dispatch] = useReducer((state, action) => {
    switch (action.type) {
      case "FETCHING":
        return { ...initialState, status: "fetching" };
      case "FETCHED":
        return {
          ...initialState,
          status: "fetched",
          data: action.payload,
          statusCode: action.code,
        };
      case "FETCH_ERROR":
        return {
          ...initialState,
          status: "error",
          error: action.payload,
        };
      default:
        return state;
    }
  }, initialState);

  useEffect(() => {
    let cancelRequest = false;
    if (!url || !url.trim()) return;

    const fetchData = async () => {
      dispatch({ type: "FETCHING" });
      if (cache.current[url]) {
        const data = cache.current[url];
        dispatch({ type: "FETCHED", payload: data });
      } else {
        try {
          await getCsrfCookie();
          const xsrfToken = getCookie("XSRF-TOKEN");
          const config = {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              "X-CSRF-TOKEN": xsrfToken,
            },
            credentials: "include",
          };
          const response = await fetch(`${apiEndpoint}${url}`, config);
          const data = await response.json();
          cache.current[url] = data;
          if (cancelRequest) return;
          dispatch({ type: "FETCHED", payload: data, code: response.status });
        } catch (error) {
          console.log({ error });
          await getCsrfCookie(); // Ensure CSRF cookie is set before any API call (except for the CSRF endpoint itself)
          dispatch({
            type: "FETCH_ERROR",
            payload: error.message,
          });
        }
      }
    };

    fetchData();

    return function cleanup() {
      cancelRequest = true;
    };
  }, [url]);

  return state;
};

export async function API(
  { endpoint = "", method = "GET", data = {} },
  authToken = null
) {
  await getCsrfCookie();
  let xsrfToken = getCookie("XSRF-TOKEN");
  const config = {
    method,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(endpoint !== "/sanctum/csrf-cookie" && xsrfToken
        ? {
            "X-CSRF-TOKEN": xsrfToken,
          }
        : {}),
    },
    credentials: "include",
  };
  if (method !== "GET") {
    config.body = JSON.stringify(data);
  }
  return fetch(`${apiEndpoint}${endpoint}`, config)
    .then(checkHttpStatus)
    .then(parseAndPreserveDetails)
    .catch((error) => {
      if (error?.response?.status === 419) {
        //redirect to login if trying to make an authenticated request without a CSRF token
        console.log("CSRF token mismatch, redirecting to login");
        window.location.href = "/login?session_expired=true";
        return;
      }
    });
}

export async function updateAvatarPost({ formData, history }) {
  await getCsrfCookie();
  const xsrfToken = getCookie("XSRF-TOKEN");
  return fetch(`${apiEndpoint}/user/image`, {
    method: "POST",
    headers: {
      "X-CSRF-TOKEN": xsrfToken,
    },
    credentials: "include",
    body: formData,
  })
    .then(() => history.push("/account"))
    .catch((error) => {
      console.log(error);
    });
}

export async function login({ email = "", password = "" }) {
  await getCsrfCookie();
  const xsrfToken = getCookie("XSRF-TOKEN");
  const config = {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-TOKEN": xsrfToken,
    },
    credentials: "include",
    body: JSON.stringify({ email, password }),
  };

  return fetch(`${apiEndpoint}/auth/login`, config)
    .then((response) =>
      response.json().then((data) => {
        console.log(data);
        return data;
      })
    )
    .catch((error) => {
      console.log(error);
    });
}

// Logout for Laravel Sanctum
export async function logout(dispatch = null) {
  // Ensure CSRF cookie is present
  await getCsrfCookie();
  const xsrfToken = getCookie("XSRF-TOKEN");
  await fetch(`${apiEndpoint}/auth/logout`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-TOKEN": xsrfToken,
    },
    credentials: "include",
  });
  // Clear userObject from global state if dispatch provided
  if (dispatch) {
    dispatch({ type: "GET_USEROBJECT", userObject: {} });
  }
  // Remove any localStorage/session items if used
  localStorage.removeItem("authUser");
  // Redirect to login
  window.location.href = "/login";
}

export async function samlAuthorise(key = "") {
  await getCsrfCookie();
  const xsrfToken = getCookie("XSRF-TOKEN");
  const config = {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-TOKEN": xsrfToken,
    },
    credentials: "include",
    body: JSON.stringify({ key }),
  };
  return fetch(`${apiEndpoint}/saml`, config)
    .then((response) => response.json())
    .catch((error) => {
      console.log(error);
    });
}

export async function register({
  first_name = "",
  last_name = "",
  email = "",
  gender = "",
  private_email = "",
  password = "",
  password_confirm = "",
  company_id = "",
  voucher = "",
  use_private_email = false,
  mobile = "",
  persona_id = null,
  leave_date = null,
  rtw_date = null,
  leave_type = "",
  user_agent = window?.navigator?.userAgent ?? "unknown",
  complete = 0,
  position = "",
  social = "",
  social_id = "",
  manager_name = "",
  manager_phone = "",
  manager_email = "",
  voucherAccepted = false,
  termsAndConditions = false,
  saml_token = "",
}) {
  await getCsrfCookie();
  const xsrfToken = getCookie("XSRF-TOKEN");
  const config = {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-TOKEN": xsrfToken,
    },
    credentials: "include",
    body: JSON.stringify({
      first_name,
      last_name,
      email,
      gender,
      private_email,
      password,
      password_confirm,
      company_id,
      mobile,
      voucher,
      use_private_email,
      persona_id,
      leave_date,
      rtw_date,
      leave_type,
      user_agent,
      complete,
      position,
      social,
      social_id,
      manager_name,
      manager_phone,
      manager_email,
      voucherAccepted,
      termsAndConditions,
      saml_token,
    }),
  };
  return fetch(`${apiEndpoint}/auth/register-v2`, config)
    .then((response) => response.json())
    .catch((error) => {
      console.error(error);
    });
}

export async function customRegister({
  id = "",
  first_name = "",
  last_name = "",
  email = "",
  gender = "",
  private_email = "",
  password = "",
  password_confirm = "",
  company_id = "",
  voucher = "",
  use_private_email = false,
  mobile = "",
  persona_id = null,
  leave_date = null,
  rtw_date = null,
  leave_type = "",
  user_agent = window?.navigator?.userAgent ?? "unknown",
  complete = 0,
  position = "",
  social = "",
  social_id = "",
  manager_name = "",
  manager_phone = "",
  manager_email = "",
  voucherAccepted = false,
  termsAndConditions = false,
  saml_token = "",
  account_email = "",
  business_unit = "",
  company_name = "",
  preferred_email = "",
}) {
  await getCsrfCookie();
  const xsrfToken = getCookie("XSRF-TOKEN");
  const config = {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-TOKEN": xsrfToken,
    },
    credentials: "include",
    body: JSON.stringify({
      first_name,
      last_name,
      email,
      gender,
      private_email,
      password,
      password_confirm,
      company_id,
      mobile,
      voucher,
      use_private_email,
      persona_id,
      leave_date,
      rtw_date,
      leave_type,
      user_agent,
      complete,
      position,
      social,
      social_id,
      manager_name,
      manager_phone,
      manager_email,
      voucherAccepted,
      termsAndConditions,
      saml_token,
      account_email,
      business_unit,
      company_name,
      preferred_email,
    }),
  };

  return fetch(`${apiEndpoint}/auth/custom-register/user/${id}`, config)
    .then((response) => {
      return response.json();
    })
    .catch((error) => {
      console.error(error);
    });
}

export async function platinumMemberRegister({
  id = "",
  first_name = "",
  last_name = "",
  email = "",
  gender = "",
  private_email = "",
  password = "",
  password_confirm = "",
  company_id = "",
  voucher = "",
  use_private_email = false,
  mobile = "",
  persona_id = null,
  leave_date = null,
  rtw_date = null,
  leave_type = "",
  user_agent = window?.navigator?.userAgent ?? "unknown",
  complete = 0,
  position = "",
  social = "",
  social_id = "",
  manager_name = "",
  manager_phone = "",
  manager_email = "",
  voucherAccepted = false,
  termsAndConditions = false,
  saml_token = "",
}) {
  await getCsrfCookie();
  const xsrfToken = getCookie("XSRF-TOKEN");
  const config = {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-TOKEN": xsrfToken,
    },
    credentials: "include",
    body: JSON.stringify({
      first_name,
      last_name,
      email,
      gender,
      private_email,
      password,
      password_confirm,
      company_id,
      mobile,
      voucher,
      use_private_email,
      persona_id,
      leave_date,
      rtw_date,
      leave_type,
      user_agent,
      complete,
      position,
      social,
      social_id,
      manager_name,
      manager_phone,
      manager_email,
      voucherAccepted,
      termsAndConditions,
      saml_token,
    }),
  };

  return fetch(
    `${apiEndpoint}/auth/platinum-member-register/user/${id}`,
    config
  )
    .then((response) => {
      return response.json();
    })
    .catch((error) => {
      console.error(error);
    });
}

export function checkHttpStatus(response) {
  if (response.status === 403 || response.status === 498) {
    return response;
  }
  if (response.status >= 200 && response.status < 300) {
    return response;
  } else {
    let error = new Error(response.statusText);
    error.response = response;
    console.log("error response", error.response);
    throw error;
  }
}

function parseAndPreserveDetails(response) {
  return new Promise((resolve, reject) => {
    let ok = response.ok;
    let status = response.status;
    response
      .json()
      .then((result) => resolve({ result, ok, status }))
      .catch((error) => {
        if (error?.message.includes("JSON.parse")) {
          resolve({ result: {}, ok, status });
        } else {
          reject();
        }
      });
  });
}
